// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { i18n } from '../providers';
import { isNaNValue, isNull, isNullOrUndefined, isUndefined } from '@shared/utils';

const getLocale = () => (i18n.language === 'en' ? 'en-US' : 'es-ES');

const isPlainNumberCurrency = (currency?: string | null) =>
  isNull(currency) || currency === '' || currency === 'N.A';

const validateNumber = (number: number | undefined | null) => {
  if (isUndefined(number)) return 0;

  if (isNull(number)) return 0;

  if (isNaNValue(number)) return 0;

  if (!isFinite(number)) return 0;

  return number;
};

const createMoneyFormatter = (currency?: string | null, digits = 0) => {
  const locale = getLocale();

  if (isPlainNumberCurrency(currency)) {
    return new Intl.NumberFormat(locale, {
      maximumFractionDigits: digits,
      minimumFractionDigits: digits,
      useGrouping: true,
    });
  }

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currency ?? 'USD',
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
    currencyDisplay: 'narrowSymbol',
    useGrouping: true,
  });
};

export const formatToMoney = (
  value: number | undefined | null,
  currency?: string | null,
  digits = 0,
) => {
  const validatedNumber = validateNumber(value);

  if (isPlainNumberCurrency(currency) && validatedNumber <= 0) {
    return '';
  }

  return createMoneyFormatter(currency, digits).format(validatedNumber);
};

export const formatMoneyRange = (
  start: number | undefined | null,
  end: number | undefined | null,
  currency?: string | null,
  digits = 0,
) => {
  const startValue = validateNumber(start);

  const endValue = validateNumber(end);

  return createMoneyFormatter(currency, digits).formatRange(startValue, endValue);
};

export const formatToPercent = (value: number | undefined | null, digits = 1) => {
  const locale = getLocale();

  const validatedNumber = validateNumber(value);

  return new Intl.NumberFormat(locale, {
    style: 'percent',
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
    useGrouping: true,
  }).format(validatedNumber);
};

export const formatToNumber = (value: number | undefined | null, digits = 2) => {
  const locale = getLocale();

  const validatedNumber = validateNumber(value);

  return new Intl.NumberFormat(locale, {
    maximumFractionDigits: digits,
    minimumFractionDigits: digits,
    useGrouping: true,
  }).format(validatedNumber);
};
